import React from 'react';

import { Box, CssBaseline, ThemeProvider } from '@mui/material';
import { Appbar } from 'components/appbar';
import { SignalRNotifications, SignalRProvider } from 'contexts/SignalRContext';
import JobSelection from 'pages/JobSelection';
import Login from 'pages/Login';
import Preview from 'pages/Preview';
import { Route, Routes, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { scodixAiTheme } from 'styles/theme';
import { useSignalRClient } from 'useSignalRClient';
import { debugLog } from 'utils/logger';

import AuthRoute from './routes/authRoute';
import PrivateRoute from './routes/privateRoute';

// Create a separate component that uses the SignalR hook
function SignalRApp() {
   const location = useLocation();

   const [sessionUserId, setSessionUserId] = React.useState<string | null>(() => {
      try {
         return sessionStorage.getItem('userId');
      } catch (e) {
         return null;
      }
   });

   // Ensure debug flag exists in localStorage on first load
   React.useEffect(() => {
      try {
         const stored = localStorage.getItem('debug_3d');
         if (stored === null) {
            localStorage.setItem('debug_3d', 'false');
         }
      } catch (e) {
         // ignore storage errors
      }
   }, []);

   const isAuthenticated = React.useMemo(() => !!sessionStorage.getItem('accessToken'), [location]);

   const syncSessionUserId = React.useCallback(() => {
      try {
         setSessionUserId(sessionStorage.getItem('userId'));
      } catch (e) {
         setSessionUserId(null);
      }
   }, []);

   React.useEffect(() => {
      window.addEventListener('session-user-updated', syncSessionUserId);
      window.addEventListener('storage', syncSessionUserId);

      return () => {
         window.removeEventListener('session-user-updated', syncSessionUserId);
         window.removeEventListener('storage', syncSessionUserId);
      };
   }, [syncSessionUserId]);

   React.useEffect(() => {
      if (!isAuthenticated) {
         setSessionUserId(null);
         return;
      }

      syncSessionUserId();
   }, [isAuthenticated, syncSessionUserId]);

   const signalRServerUrl = React.useMemo(() => {
      const rawUrl = process.env.REACT_APP_SIGNALR_SERVER_URL;
      if (!rawUrl) {
         console.error('REACT_APP_SIGNALR_SERVER_URL is not defined');
         return '';
      }
      return rawUrl.replace(/\/$/, '');
   }, []);

   const signalRClientId = React.useMemo(() => {
      return sessionUserId ? sessionUserId.trim() : '';
   }, [sessionUserId]);

   const { startAsync, stopAsync, pingAsync } = useSignalRClient(signalRServerUrl, signalRClientId);

   React.useEffect(() => {
      if (!isAuthenticated) {
         void stopAsync();
         return;
      }

      if (!signalRServerUrl || !signalRClientId) {
         return;
      }

      void startAsync();

      return () => {
         void stopAsync();
      };
   }, [isAuthenticated, signalRClientId, signalRServerUrl, startAsync, stopAsync]);

   React.useEffect(() => {
      const onPing = async () => {
         const result = await pingAsync();
         if (result != null) {
            debugLog(`[SignalR] Ping response: ${result}`);
         }
      };

      window.addEventListener('signalr-ping', onPing as EventListener);
      return () => {
         window.removeEventListener('signalr-ping', onPing as EventListener);
      };
   }, [pingAsync]);

   return (
      <ThemeProvider theme={scodixAiTheme}>
         <CssBaseline />
         {isAuthenticated ? <Appbar /> : null}
         <Box
            sx={{
               display: 'flex',
               width: '100%',
               minHeight: '100vh',
               pt: isAuthenticated ? '50px' : 0,
               // Keeps Preview MainWrapper height in sync with fixed AppBar (shared preview has no bar)
               '--app-chrome-top': isAuthenticated ? '50px' : '0px',
            }}
         >
            <Box component='main' sx={{ flex: 1 }}>
               <Routes>
                  <Route path='/' element={<AuthRoute component={Login} />} />
                  <Route path='/upload' element={<PrivateRoute component={JobSelection} />} />
                  <Route path='/preview' element={<PrivateRoute component={Preview} />} />
                  <Route path='/preview/shared' element={<Preview />} />
               </Routes>
            </Box>
         </Box>

         <SignalRNotifications />
         <ToastContainer />
      </ThemeProvider>
   );
}

function App() {
   return (
      <SignalRProvider>
         <SignalRApp />
      </SignalRProvider>
   );
}

export default App;
